import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setSchemas,
  setSelectedSchema,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addSchema,
  updateSchema as updateSchemaAction,
  removeSchema,
  clearSelectedSchema,
} from '../../store/slices/clarificationSchemasSlice';
import {
  GET_CLARIFICATION_SCHEMAS_LIST,
  GET_CLARIFICATION_SCHEMA,
} from '../../graphql/queries/clarificationSchemas.queries';
import {
  CREATE_CLARIFICATION_SCHEMA,
  UPDATE_CLARIFICATION_SCHEMA,
  DELETE_CLARIFICATION_SCHEMA,
} from '../../graphql/mutations/clarificationSchemas.mutations';
import {
  FindClarificationSchemaListInput,
  FindClarificationSchemaInput,
  CreateClarificationSchemaInput,
  UpdateClarificationSchemaInput,
  DeleteClarificationSchemaInput,
  ClarificationSchemaListResponse,
  ClarificationSchemaResponse,
  ClarificationSchemaMutationResponse,
  DeleteClarificationSchemaResponse,
  ClarificationSchema,
} from '../../types/clarificationSchemas.types';
import ViewClarificationSchemaModal from '../../components/ClarificationSchemas/ViewClarificationSchemaModal';
import CreateEditClarificationSchemaModal, {
  ClarificationSchemaFormData,
} from '../../components/ClarificationSchemas/CreateEditClarificationSchemaModal';
import ConfirmDeleteClarificationSchemaDialog from '../../components/ClarificationSchemas/ConfirmDeleteClarificationSchemaDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './ClarificationSchemas.css';

const ClarificationSchemas: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { schemas, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.clarificationSchemas
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState<ClarificationSchema | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchSchemas, { loading: fetchingList }] = useLazyQuery<
    { clarificationSchemas: ClarificationSchemaListResponse },
    { params: FindClarificationSchemaListInput }
  >(GET_CLARIFICATION_SCHEMAS_LIST, { fetchPolicy: 'network-only' });

  const [fetchSchema] = useLazyQuery<
    { clarificationSchema: ClarificationSchemaResponse },
    { params: FindClarificationSchemaInput }
  >(GET_CLARIFICATION_SCHEMA, { fetchPolicy: 'network-only' });

  const [createSchemaMutation] = useMutation<
    { createClarificationSchema: ClarificationSchemaMutationResponse },
    { params: CreateClarificationSchemaInput }
  >(CREATE_CLARIFICATION_SCHEMA);

  const [updateSchemaMutation] = useMutation<
    { updateClarificationSchema: ClarificationSchemaMutationResponse },
    { params: UpdateClarificationSchemaInput }
  >(UPDATE_CLARIFICATION_SCHEMA);

  const [deleteSchemaMutation] = useMutation<
    { deleteClarificationSchema: DeleteClarificationSchemaResponse },
    { params: DeleteClarificationSchemaInput }
  >(DELETE_CLARIFICATION_SCHEMA);

  useEffect(() => {
    loadSchemas();
  }, [pagination.page, pagination.pageSize, search, sort]);

  const loadSchemas = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchSchemas({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.clarificationSchemas?.success && data.clarificationSchemas.data) {
        dispatch(setSchemas(data.clarificationSchemas.data));
      } else {
        dispatch(setError(data?.clarificationSchemas?.message || 'Error al cargar esquemas'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'desc' }));
    } else {
      dispatch(setSort({ field: 'createdAt', order: 'desc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchSchema({ variables: { params: { ids: [id] } } });
      if (data?.clarificationSchema?.success && data.clarificationSchema.data && data.clarificationSchema.data.length > 0) {
        dispatch(setSelectedSchema(data.clarificationSchema.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.clarificationSchema?.message || 'Error al cargar esquema'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchSchema({ variables: { params: { ids: [id] } } });
      if (data?.clarificationSchema?.success && data.clarificationSchema.data && data.clarificationSchema.data.length > 0) {
        dispatch(setSelectedSchema(data.clarificationSchema.data[0]));
        setIsEdit(true);
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.clarificationSchema?.message || 'Error al cargar esquema'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedSchema());
    setIsEdit(false);
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: ClarificationSchemaFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (isEdit && formData.id) {
        const { data: responseData } = await updateSchemaMutation({
          variables: {
            params: {
              id: formData.id,
              entity: formData.entity,
              matchEntities: formData.matchEntities,
              type: formData.type,
              relatedEntities: formData.relatedEntities,
              finalQueryTemplate: formData.finalQueryTemplate,
              personKeywords: formData.personKeywords,
              description: formData.description,
              triggerKeywords: formData.triggerKeywords,
              maxResultsWithoutClarification: formData.maxResultsWithoutClarification,
              parentSchemaId: formData.parentSchemaId,
            },
          },
        });

        if (responseData?.updateClarificationSchema?.success) {
          if (responseData.updateClarificationSchema.data) {
            dispatch(updateSchemaAction(responseData.updateClarificationSchema.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedSchema());
          setSaveError(null);
          setSuccessMessage('Esquema de clarificación actualizado exitosamente');
        } else {
          const msg = responseData?.updateClarificationSchema?.message || 'Error al actualizar esquema';
          const errs = responseData?.updateClarificationSchema?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await createSchemaMutation({
          variables: {
            params: {
              entity: formData.entity,
              matchEntities: formData.matchEntities,
              type: formData.type,
              relatedEntities: formData.relatedEntities,
              finalQueryTemplate: formData.finalQueryTemplate,
              personKeywords: formData.personKeywords,
              description: formData.description,
              triggerKeywords: formData.triggerKeywords,
              maxResultsWithoutClarification: formData.maxResultsWithoutClarification,
              parentSchemaId: formData.parentSchemaId,
            },
          },
        });

        if (responseData?.createClarificationSchema?.success) {
          if (responseData.createClarificationSchema.data) {
            dispatch(addSchema(responseData.createClarificationSchema.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedSchema());
          setSaveError(null);
          setSuccessMessage('Esquema de clarificación creado exitosamente');
        } else {
          const msg = responseData?.createClarificationSchema?.message || 'Error al crear esquema';
          const errs = responseData?.createClarificationSchema?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (schema: ClarificationSchema) => {
    setSchemaToDelete(schema);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!schemaToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteSchemaMutation({
        variables: {
          params: {
            ids: [schemaToDelete.id],
          },
        },
      });

      if (responseData?.deleteClarificationSchema?.success) {
        dispatch(removeSchema(schemaToDelete.id));
        setDeleteDialogOpen(false);
        setSchemaToDelete(null);
        setDeleteError(null);
        setSuccessMessage('Esquema de clarificación eliminado exitosamente');
      } else {
        const msg = responseData?.deleteClarificationSchema?.message || 'Error al eliminar esquema';
        const errs = responseData?.deleteClarificationSchema?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedSchema());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedSchema());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSchemaToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<ClarificationSchema>[] = [
    {
      field: 'entity',
      headerName: 'Entidad',
      width: 150,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/clarification-schemas/${params.row.id}/fields`)}
          sx={{ cursor: 'pointer' }}
        >
          {params.row.entity}
        </Link>
      ),
    },
    { field: 'type', headerName: 'Tipo', width: 110 },
    {
      field: 'matchEntities',
      headerName: 'Match Entities',
      width: 180,
      valueGetter: (value: string[]) => value.join(', '),
    },
    {
      field: 'relatedEntities',
      headerName: 'Related Entities',
      width: 180,
      valueGetter: (value: string[]) => value.join(', '),
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/clarification-schemas/${params.row.id}/fields`)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {params.row.description}
        </Link>
      ),
    },
    {
      field: 'triggerKeywords',
      headerName: 'Palabras Clave',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string[]) => value.join(', '),
    },
    { field: 'maxResultsWithoutClarification', headerName: 'Máx. Resultados', width: 130 },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="clarification-schemas-container">
        <Paper elevation={2} className="clarification-schemas-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Clarificación</Typography>
          </Breadcrumbs>

          <Box className="clarification-schemas-header">
            <Typography variant="h4" component="h1">
              Esquemas de Clarificación
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de esquemas de clarificación para consultas
            </Typography>
          </Box>

          <Box className="clarification-schemas-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar esquema..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
              }}
            >
              Agregar Clarificación
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={schemas}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewClarificationSchemaModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditClarificationSchemaModal
          open={createEditModalOpen}
          isEdit={isEdit}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteClarificationSchemaDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteConfirm}
          entityName={schemaToDelete?.entity || ''}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default ClarificationSchemas;
